import { FC, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

import { Separator } from "@/components/_atoms/separator";
import { cn } from "@/lib/cn";
import { useLeftSidebarStore } from "@/stores/left-aside-store";
import { useRightAsideStore } from "@/stores/right-aside-store";

import { SidebarToggleButton } from "./sidebar/sidebar-toggle-button";
import { useLayout } from "./use-layout";

interface HeaderPropsWithChildren extends PropsWithChildren {
  className?: string;
  height?: string;
  showRightAsideButton?: boolean;
  showLeftSidebarButton?: boolean;
}

export const HeaderLayout: FC<HeaderPropsWithChildren> = ({
  children,
  className,
  height = "chrome-row",
  showRightAsideButton = false,
  showLeftSidebarButton = true,
}) => {
  const { t } = useTranslation("common");
  const { hasSidebar } = useLayout();
  const { isHidden: isLeftSidebarHidden, toggle: toggleLeftSidebar } = useLeftSidebarStore();
  const { isHidden: isRightAsideHidden, toggle: toggleRightAside } = useRightAsideStore();

  const hasSidebarIcon = showLeftSidebarButton && hasSidebar;

  return (
    <header
      className={cn(
        `no-scrollbar relative flex w-full max-w-full ${height} shrink-0 items-center gap-2 overflow-x-scroll border-b-[0.5px] px-[var(--chrome-gutter)] text-sm`,
        hasSidebarIcon ? "pl-6" : "pl-5",
        "justify-between",
        className,
      )}
    >
      {hasSidebarIcon && (
        <div className="flex items-center gap-2">
          <SidebarToggleButton
            isOpen={!isLeftSidebarHidden}
            onToggle={toggleLeftSidebar}
            openLabel={t("sidebar.openSidebar")}
            closeLabel={t("sidebar.closeSidebar")}
          />
          <Separator orientation="vertical" className="h-4" />
        </div>
      )}
      {children}
      {showRightAsideButton && (
        <div className="flex items-center gap-2">
          <Separator orientation="vertical" className="h-4" />
          <SidebarToggleButton
            isOpen={!isRightAsideHidden}
            onToggle={toggleRightAside}
            openLabel={t("sidebar.openRightSidebar")}
            closeLabel={t("sidebar.closeSidebar")}
            flip
          />
        </div>
      )}
    </header>
  );
};
